import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMedia } from "react-use";

import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { TextListWithDot } from "@/components/TextListWithDot";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IAssignment } from "@/data/assignments";
import { formatFullName } from "@/data/persons";
import { putTaskAction } from "@/data/tasks";
import { IWorkOrder, WorkOrderStatus } from "@/data/work-orders";
import { Flex, Header, Tooltip } from "@/eworkitComponents/components";
import { useFormats } from "@/hooks/Formats";
import { useToasts } from "@/views/components/Toast/useToasts";
import { WorkOrderStatusBadge } from "@/views/components/WorkOrder/WorkOrderStatusBadge";
import { router } from "@/views/router";
import { AcceptTaskModalButton, RejectTaskModalButton } from "@/views/Tasks/List/components/ConfirmationModals";

function isAssignment(resource: any): resource is IAssignment {
  return Boolean(resource?.assignmentConsultantProfile);
}

export interface IWorkOrderHeaderProps {
  resource: IWorkOrder | IAssignment;
  update?: () => void;
  hideActionButtons?: boolean;
  translationNamespace?: "WORK_ORDERS" | "ASSIGNMENTS";
}

export const WorkOrderHeader: FC<IWorkOrderHeaderProps> = memo(
  ({ resource, hideActionButtons = false, update, translationNamespace = "WORK_ORDERS", children }) => {
    const { t } = useTranslation(["session", "common"]);
    const { formatDate } = useFormats();
    const history = useHistory();
    const { addToast } = useToasts();
    const { contextCompany } = useContext(UserContext);
    const isSmallScreen = useMedia(MEDIA_SIZE_SM);
    const startDate = isAssignment(resource) ? resource.initialStartDate : resource.startDate;
    const endDate = resource.actualEndDate ?? resource.endDate;

    const handleAccept = useCallback(
      async (comment: string) => {
        try {
          await putTaskAction(
            { companyId: contextCompany?.id, taskId: resource.tasks[0].id },
            {
              action: "accept",
              comment,
            },
          );

          update?.();

          addToast({ text: t(`${translationNamespace}.HEADER.ACTION_ACCEPTED_MESSAGE`), type: "success" });
        } catch {
          addToast({ text: t("common:VALIDATION_MESSAGES.GENERAL"), type: "warning" });
        }
      },
      [resource.tasks, update],
    );

    const handleReject = useCallback(
      async (comment: string) => {
        try {
          await putTaskAction(
            { companyId: contextCompany?.id, taskId: resource.tasks[0].id },
            {
              action: "reject",
              comment,
            },
          );

          history.push(router.get("assignments").path);

          addToast({ text: t(`${translationNamespace}.HEADER.ACTION_REJECTED_MESSAGE`), type: "success" });
        } catch {
          addToast({ text: t("common:VALIDATION_MESSAGES.GENERAL"), type: "warning" });
        }
      },
      [resource.tasks, history],
    );

    return (
      <Section
        width={12}
        padding={["lg", "lg", false]}
        flex
        borderRadius={!isSmallScreen}
        shadow={isSmallScreen ? "none" : "default"}>
        <Flex grow={0} direction={isSmallScreen ? "column" : "row"} innerMargin="md">
          <Flex grow={1} direction="row" innerMargin="lg">
            <Icon size="xxl" icon="file-tasks-1" />

            <Flex grow={0} direction="column" align="flex-start" innerMargin="sm">
              <Flex align="center" wrap="wrap">
                <Flex margin={[false, "md", false, false]}>
                  <Header
                    tagType="h1"
                    size={isSmallScreen ? "md" : "lg"}
                    text={formatFullName(resource.consultantProfile)}
                    weight="bold"
                  />
                </Flex>

                <Flex margin={["xs", false]}>
                  <WorkOrderStatusBadge status={resource.status} />
                </Flex>
              </Flex>

              <TextListWithDot>
                {!!resource.systemId && <div>{resource.systemId}</div>}
                <Tooltip
                  placement="bottom"
                  content={t(`${translationNamespace}.HEADER.PERIOD_TOOLTIP`, {
                    startDate: formatDate(startDate),
                    endDate: formatDate(endDate),
                  })}>
                  <Flex key="dates" innerMargin="xs">
                    <Text color="black" text={formatDate(startDate)} />
                    <Icon icon="ic_arrow_forward" color="grey1" />
                    <Text color="black" text={formatDate(endDate)} />
                  </Flex>
                </Tooltip>
              </TextListWithDot>
            </Flex>
          </Flex>

          {!hideActionButtons && resource.status === WorkOrderStatus.CONFIRMATION_PENDING && (
            <Flex grow={0} direction="row" justify="flex-start" innerMargin="sm">
              {resource.canBeRejected && (
                <RejectTaskModalButton
                  variant="secondary"
                  size="lg"
                  display={isSmallScreen ? "block" : "inline-block"}
                  onReject={handleReject}
                  openableProps={{
                    header: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.HEADER`),
                    confirmationText: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.REJECT_TASK_QUESTION`),
                    cancelButtonLabel: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.BUTTON_REJECT`),
                    acceptButtonLabel: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.BUTTON_CONFIRM`),
                  }}>
                  {t(`${translationNamespace}.HEADER.REJECT_BUTTON`)}
                </RejectTaskModalButton>
              )}

              {resource.canBeAccepted && (
                <AcceptTaskModalButton
                  variant="primary"
                  size="lg"
                  display={isSmallScreen ? "block" : "inline-block"}
                  onAccept={handleAccept}
                  openableProps={{
                    header: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.HEADER`),
                    confirmationText: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.CONFIRM_TASK_QUESTION`),
                    cancelButtonLabel: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.BUTTON_REJECT`),
                    acceptButtonLabel: t(`${translationNamespace}.HEADER.CONFIRMATION_DIALOG.BUTTON_CONFIRM`),
                  }}>
                  {t(`${translationNamespace}.HEADER.ACCEPT_BUTTON`)}
                </AcceptTaskModalButton>
              )}
            </Flex>
          )}
        </Flex>

        {children}
      </Section>
    );
  },
);
